import StatTile from '@/components/dashboard/StatTile'
import { format, startOfWeek, addDays, isToday } from 'date-fns'
import { ChevronLeft, ChevronRight, Clock, History, LogIn, LogOut, Home, Calendar, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMyHistory } from '@/api/attendance'
import { getMyLeaves } from '@/api/leave'
import { useAuthStore } from '@/stores/authStore'
import { useParams, useSearchParams } from 'react-router-dom'
import { authApi } from '@/api/auth'
import AttendanceHistory from '@/components/attendance/AttendanceHistory'

function formatTime(isoStr?: string): string {
  if (!isoStr) return '—'
  return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function formatDuration(minutes?: number): string {
  if (!minutes || minutes <= 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

type DayStatus =
  | 'completed'
  | 'overtime'
  | 'less'
  | 'active'
  | 'leave'
  | 'wfh'
  | 'not_in'
  | 'off'
  | 'future'

function StatusBadge({ status, leaveType }: { status: DayStatus; leaveType?: string }) {
  const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium'
  switch (status) {
    case 'completed':
      return <span className={`${base} bg-green-100 text-green-700`}><CheckCircle2 className="h-3 w-3" />Completed</span>
    case 'overtime':
      return <span className={`${base} bg-indigo-100 text-indigo-700`}><TrendingUp className="h-3 w-3" />Overtime</span>
    case 'less':
      return <span className={`${base} bg-amber-100 text-amber-700`}><AlertCircle className="h-3 w-3" />Less Hours</span>
    case 'active':
      return <span className={`${base} bg-blue-100 text-blue-700`}><LogIn className="h-3 w-3" />Active</span>
    case 'leave': {
      const label = leaveType ? `${leaveType.charAt(0).toUpperCase() + leaveType.slice(1)} Leave` : 'Leave'
      return <span className={`${base} bg-orange-100 text-orange-700`}><Calendar className="h-3 w-3" />{label}</span>
    }
    case 'wfh':
      return <span className={`${base} bg-teal-100 text-teal-700`}><Home className="h-3 w-3" />WFH</span>
    case 'not_in':
      return <span className={`${base} bg-red-100 text-red-700`}><LogOut className="h-3 w-3" />Absent</span>
    case 'off':
      return <span className={`${base} bg-gray-100 text-gray-500`}>Off</span>
    default:
      return <span className="text-xs text-gray-400">—</span>
  }
}

const DAY_ABBREVS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

type TimesheetTab = 'week' | 'history'

/** Timesheet: this week's hours, plus the employee's full attendance history
 *  (weekly / monthly / yearly / custom). */
export default function Timesheet() {
  const [params, setParams] = useSearchParams()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { user } = useAuthStore()
  const { data: features } = useQuery({
    queryKey: ['features', orgSlug], queryFn: authApi.getFeatures, staleTime: 60_000, enabled: !!user,
  })
  const historyOn = !features || features.includes('attendance')
  const tab: TimesheetTab = params.get('tab') === 'history' && historyOn ? 'history' : 'week'
  const setTab = (t: TimesheetTab) => setParams(t === 'history' ? { tab: 'history' } : {}, { replace: true })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Timesheet</h1>
        <p className="text-sm text-gray-500">Your hours this week and your full attendance history.</p>
      </div>
      {historyOn && (
        <div className="flex gap-6 border-b border-gray-200">
          {([['week', 'This Week', Clock], ['history', 'Attendance History', History]] as const).map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 pb-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${tab === key ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      )}
      {tab === 'history' ? <AttendanceHistory /> : <WeekTimesheet />}
    </div>
  )
}

function WeekTimesheet() {
  const { user } = useAuthStore()
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  )

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i))
  const startStr = format(currentWeekStart, 'yyyy-MM-dd')
  const endStr = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd')

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['attendance', 'my-history', startStr, endStr],
    queryFn: () => getMyHistory(startStr, endStr),
  })

  const { data: leaves = [] } = useQuery({
    queryKey: ['leave', 'me', startStr, endStr],
    queryFn: () => getMyLeaves(startStr, endStr),
  })

  // User's shift configuration (per-day, with fallback to simple fields)
  const parseTime = (t?: string, fallback = '09:00') => {
    const [h, m] = (t || fallback).split(':').map(Number)
    return h * 60 + m
  }
  const defaultStartMin = parseTime(user?.standup_time, '09:00')
  const defaultEndMin = parseTime(user?.eod_time, '17:00')
  const simpleShiftDays = new Set(
    (user?.shift_days || 'mon,tue,wed,thu,fri').split(',').map(s => s.trim().toLowerCase())
  )

  // Compute shift duration in minutes, supporting overnight shifts (end < start)
  const computeDurationMin = (startMin: number, endMin: number): number => {
    if (endMin === startMin) return 0
    if (endMin < startMin) {
      // Overnight: wraps past midnight
      return 24 * 60 - startMin + endMin
    }
    return endMin - startMin
  }

  // Resolve shift info for a given day abbreviation (mon..sun)
  const getShiftForDay = (dayAbbrev: string): { durationMin: number; enabled: boolean } => {
    const schedule = user?.shift_schedule
    if (schedule && schedule[dayAbbrev]) {
      const cfg = schedule[dayAbbrev]
      const startMin = parseTime(cfg.start, '09:00')
      const endMin = parseTime(cfg.end, '17:00')
      const duration = Math.max(computeDurationMin(startMin, endMin), 60)
      return { durationMin: duration, enabled: Boolean(cfg.enabled) }
    }
    return {
      durationMin: Math.max(computeDurationMin(defaultStartMin, defaultEndMin), 60),
      enabled: simpleShiftDays.has(dayAbbrev),
    }
  }

  // Map leaves by date for quick lookup
  const leaveByDate = new Map<string, string>()
  for (const l of leaves) {
    leaveByDate.set(l.date, l.type)
  }

  // Aggregate records by date — sum durations, earliest checkin, latest checkout
  interface DaySummary {
    date: string
    checkin_time: string | undefined
    checkout_time: string | undefined
    duration_minutes: number
    status: string
    sessions: number
  }
  const byDate = new Map<string, DaySummary>()
  for (const r of records) {
    const existing = byDate.get(r.date)
    const rDuration = r.duration_minutes
      ?? (r.status === 'active' && r.checkin_time
        ? Math.floor((Date.now() - new Date(r.checkin_time).getTime()) / 60000)
        : 0)
    if (!existing) {
      byDate.set(r.date, {
        date: r.date,
        checkin_time: r.checkin_time,
        checkout_time: r.checkout_time,
        duration_minutes: rDuration,
        status: r.status,
        sessions: 1,
      })
    } else {
      existing.duration_minutes += rDuration
      existing.sessions += 1
      // Keep earliest checkin
      if (r.checkin_time && (!existing.checkin_time || r.checkin_time < existing.checkin_time)) {
        existing.checkin_time = r.checkin_time
      }
      // Keep latest checkout
      if (r.checkout_time && (!existing.checkout_time || r.checkout_time > existing.checkout_time)) {
        existing.checkout_time = r.checkout_time
      }
      // If any session is still active, mark day as active
      if (r.status === 'active') {
        existing.status = 'active'
      }
    }
  }

  const uniqueDays = Array.from(byDate.values())
  const weekTotal = uniqueDays.reduce((sum, d) => sum + d.duration_minutes, 0)

  // Compute status for a given day
  const computeDayStatus = (dateStr: string, dayOfWeek: number, isFuture: boolean): { status: DayStatus; leaveType?: string } => {
    if (isFuture) return { status: 'future' }
    const dayAbbrev = DAY_ABBREVS[dayOfWeek]
    const { durationMin: dayShiftMin, enabled: dayEnabled } = getShiftForDay(dayAbbrev)
    const lessThreshold = Math.floor(dayShiftMin * 0.9)
    const leaveType = leaveByDate.get(dateStr)
    const daySummary = byDate.get(dateStr)

    // Leave / WFH takes precedence (unless user actually worked)
    if (leaveType && !daySummary) {
      if (leaveType === 'wfh') return { status: 'wfh' }
      return { status: 'leave', leaveType }
    }

    if (!daySummary) {
      // No attendance and no leave
      if (!dayEnabled) return { status: 'off' }
      return { status: 'not_in' }
    }

    if (daySummary.status === 'active') return { status: 'active' }

    // Completed session — classify by duration using this day's shift
    if (daySummary.duration_minutes > dayShiftMin) return { status: 'overtime' }
    if (daySummary.duration_minutes < lessThreshold) return { status: 'less' }
    return { status: 'completed' }
  }

  // Week stats
  const completedDays = weekDays.filter((day) => {
    const dateStr = format(day, 'yyyy-MM-dd')
    const isFuture = day > new Date() && !isToday(day)
    const { status } = computeDayStatus(dateStr, day.getDay() === 0 ? 6 : day.getDay() - 1, isFuture)
    return status === 'completed' || status === 'overtime'
  }).length

  const goToPreviousWeek = () => setCurrentWeekStart((prev) => addDays(prev, -7))
  const goToNextWeek = () => setCurrentWeekStart((prev) => addDays(prev, 7))
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm font-semibold text-gray-700">
          Week of {format(currentWeekStart, 'MMM d')} – {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={goToPreviousWeek} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={goToCurrentWeek} className="px-3 py-1.5 text-sm font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors">
            This Week
          </button>
          <button onClick={goToNextWeek} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatTile label="Total Hours" value={formatDuration(weekTotal)} tone="blue" />
        <StatTile label="Days Worked" value={uniqueDays.length} tone="green" />
        <StatTile
          label="Avg per Day"
          value={uniqueDays.length > 0 ? formatDuration(Math.round(weekTotal / uniqueDays.length)) : '—'}
          tone="amber"
        />
        <StatTile label="Completed" value={`${completedDays}/${uniqueDays.length}`} tone="purple" />
      </div>

      {/* Daily Breakdown */}
      <div className="table-wrap">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : records.length === 0 && leaves.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No attendance this week</h3>
            <p className="text-sm text-gray-500">Check in from the dashboard to start tracking</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check In</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check Out</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {weekDays.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd')
                const daySummary = byDate.get(dateStr)
                const today = isToday(day)
                const isFuture = day > new Date() && !today
                // Convert JS day (Sun=0, Mon=1..Sat=6) to our index (Mon=0..Sun=6)
                const dayIdx = day.getDay() === 0 ? 6 : day.getDay() - 1
                const { status, leaveType } = computeDayStatus(dateStr, dayIdx, isFuture)

                return (
                  <tr key={dateStr} className={today ? 'bg-brand/5' : isFuture ? 'opacity-40' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className={`text-sm font-medium ${today ? 'text-brand-dark' : 'text-gray-900'}`}>
                          {format(day, 'EEEE')}
                          {today && <span className="ml-2 text-xs text-brand font-normal">Today</span>}
                        </p>
                        <p className="text-xs text-gray-500">{format(day, 'MMM d')}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {status === 'future' ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={status} leaveType={leaveType} />
                          {daySummary && daySummary.sessions > 1 && (
                            <span className="text-xs text-gray-400">×{daySummary.sessions}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                      {formatTime(daySummary?.checkin_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                      {daySummary?.status === 'active' ? (
                        <span className="text-green-600 text-xs font-sans font-medium">In progress</span>
                      ) : formatTime(daySummary?.checkout_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {daySummary ? formatDuration(daySummary.duration_minutes) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
